import {
  ResponsiveTable,
  ResponsiveTableBody,
  ResponsiveTableCell,
  ResponsiveTableHead,
  ResponsiveTableHeader,
  ResponsiveTableRow,
} from "@/components/ui/responsive-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { ArrowUpDown } from "lucide-react";
import {
  EventStatusEnum,
  statusColors,
  statusLabels,
} from "@/types/eventStatus";

export interface Bid {
  id: string; // Changed to string to avoid duplicate parseInt issues
  contactName: string;
  companyName: string;
  startTime: Date;
  bidStatus: EventStatusEnum;
}

interface ActiveBidsTableProps {
  bids?: Bid[];
  onSortColumn: (column: keyof Bid) => void;
  sortColumn: keyof Bid;
  sortDirection: "asc" | "desc";
}

export function ActiveBidsTable({
  bids,
  onSortColumn,
  sortColumn,
  sortDirection,
}: ActiveBidsTableProps) {
  const getStatusBadge = (status: EventStatusEnum) => {
    const color = statusColors[status];
    return <Badge className={color}>{statusLabels[status]}</Badge>;
  };

  const getSortIcon = (column: keyof Bid) => {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="ml-1 p-0 h-auto"
        onClick={() => onSortColumn(column)}
      >
        <ArrowUpDown
          className={`h-4 w-4 ${
            sortColumn === column ? "text-foreground" : "text-foreground"
          }`}
        />
        <span className="sr-only">Sort by {column}</span>
      </Button>
    );
  };

  return (
    <ResponsiveTable className="text-muted-foreground">
      <ResponsiveTableHeader>
        <ResponsiveTableRow>
          <ResponsiveTableHead className="cursor-pointer">
            Name {getSortIcon("contactName")}
          </ResponsiveTableHead>
          <ResponsiveTableHead className="cursor-pointer">
            Company {getSortIcon("companyName")}
          </ResponsiveTableHead>
          <ResponsiveTableHead className="cursor-pointer">
            Event Date {getSortIcon("startTime")}
          </ResponsiveTableHead>
          <ResponsiveTableHead className="cursor-pointer">
            Status {getSortIcon("bidStatus")}
          </ResponsiveTableHead>
          <ResponsiveTableHead className="text-right">
            Actions
          </ResponsiveTableHead>
        </ResponsiveTableRow>
      </ResponsiveTableHeader>
      <ResponsiveTableBody>
        {bids?.map((bid) => (
          <ResponsiveTableRow
            key={bid.id}
            mobileTitle={`${bid.contactName} - ${bid.companyName}`}
          >
            <ResponsiveTableCell
              mobileLabel="Contact Name"
              mobilePriority="high"
            >
              {bid.contactName}
            </ResponsiveTableCell>
            <ResponsiveTableCell mobileLabel="Company" mobilePriority="high">
              {bid.companyName}
            </ResponsiveTableCell>
            <ResponsiveTableCell
              mobileLabel="Event Date"
              mobilePriority="medium"
            >
              {format(new Date(bid.startTime), "PPp")}
            </ResponsiveTableCell>
            <ResponsiveTableCell mobileLabel="Status" mobilePriority="medium">
              {getStatusBadge(bid.bidStatus)}
            </ResponsiveTableCell>
            <ResponsiveTableCell className="text-right">
              <Link to={`/admin/bids/${bid.id}`}>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  Details
                </Button>
              </Link>
            </ResponsiveTableCell>
          </ResponsiveTableRow>
        ))}
        {(!bids || bids.length === 0) && (
          <ResponsiveTableRow>
            <ResponsiveTableCell
              colSpan={6}
              className="text-center py-6 text-muted-foreground"
            >
              No bids found
            </ResponsiveTableCell>
          </ResponsiveTableRow>
        )}
      </ResponsiveTableBody>
    </ResponsiveTable>
  );
}
